/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { pushEvent } from '../data-layer';
import { buildUaProduct } from './item-shape';
import type { ShopperBasketsV2 } from '@/scapi';

type Basket = ShopperBasketsV2.schemas['Basket'];
type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

export interface GlobalSessionData {
    loggedInState: 'logged-in' | 'guest';
    basket?: Basket;
    locale: string;
    /** Customer ID (demandwareID) — legacy parity. Only set when user is registered. */
    customerId?: string;
    /**
     * SHA-256 hashed email. Must be computed server-side for security.
     * Client never receives raw email for hashing. Provide via server endpoint or backend push.
     */
    emailsha256?: string;
    /**
     * INT-21 Phase F — session ZIP (legacy `dimension17`). Sourced from
     * `useDeliveryZip().zip` in `GtmProvider`. When absent (guest with no ZIP
     * entered, cookie not yet resolved) the push omits both the legacy
     * `dimension17` alias and the GA4 `user_properties.zip_code` field
     * entirely — no fabricated blanks.
     */
    zip?: string;
    /**
     * INT-21 Phase F — hasOrders bool sourced from a server round-trip
     * through `/action/gtm-user-context` (SCAPI `getCustomerOrders limit=1`).
     * Undefined means "not yet resolved" or "guest" — the push omits both
     * the legacy `dimension21` alias and the GA4
     * `user_properties.is_returning_customer` field in that case.
     */
    isReturningCustomer?: boolean;
}

/**
 * Full Ashley cart-type taxonomy. Broadened from the initial Delivery/Pickup/Mixed
 * split so marketing can slice by delivery method the same way GA3 did.
 *
 * - `Home Delivery`         — legacy `HD`   (white-glove home delivery)
 * - `Doorstep Delivery`     — legacy `PHD`  (parcel home delivery)
 * - `Truck Home Delivery`   — legacy `THD`  (Ashley Doorstep Delivery / truck)
 * - `Direct Ship`           — legacy `DS` / `DS3PL` (small parcel or 3PL)
 * - `Pickup`                — BOPIS
 * - `Mixed`                 — any combination of pickup + delivery
 */
export type CartType =
    | 'Home Delivery'
    | 'Doorstep Delivery'
    | 'Truck Home Delivery'
    | 'Direct Ship'
    | 'Pickup'
    | 'Mixed';

/**
 * Legacy short-code alias for GA3 report parity. Emitted alongside the human
 * label on the same push (as `cart_type_code`) when `emitLegacyAliases !== false`.
 */
export type CartTypeCode = 'HD' | 'PHD' | 'THD' | 'DS' | 'PICKUP' | 'MIXED';

/**
 * Legacy `c_deliveryMethod` values Ashley writes on each `ProductItem`.
 * `DS3PL` collapses onto `DS` for the numeric alias — GA3 didn't split 3PL.
 */
type DeliveryMethodCode = 'HD' | 'PHD' | 'THD' | 'DS' | 'DS3PL';

const CART_TYPE_LABELS: Record<DeliveryMethodCode, CartType> = {
    HD: 'Home Delivery',
    PHD: 'Doorstep Delivery',
    THD: 'Truck Home Delivery',
    DS: 'Direct Ship',
    DS3PL: 'Direct Ship',
};

const CART_TYPE_CODES: Record<CartType, CartTypeCode> = {
    'Home Delivery': 'HD',
    'Doorstep Delivery': 'PHD',
    'Truck Home Delivery': 'THD',
    'Direct Ship': 'DS',
    Pickup: 'PICKUP',
    Mixed: 'MIXED',
};

function isDeliveryMethodCode(value: unknown): value is DeliveryMethodCode {
    return value === 'HD' || value === 'PHD' || value === 'THD' || value === 'DS' || value === 'DS3PL';
}

function readDeliveryMethod(item: ProductItem): DeliveryMethodCode | undefined {
    const raw = (item as unknown as Record<string, unknown>).c_deliveryMethod;
    return isDeliveryMethodCode(raw) ? raw : undefined;
}

function shipmentIsPickup(shipment: ShopperBasketsV2.schemas['Shipment']): boolean {
    return Boolean((shipment as { c_fromStoreId?: string }).c_fromStoreId);
}

/**
 * Returns the map of pickup vs delivery shipments so the caller can decide
 * whether a line's `c_deliveryMethod` should map to `Pickup` (line belongs
 * to a pickup shipment) or a delivery method.
 */
function buildPickupShipmentIdSet(basket: Basket): Set<string> {
    const ids = new Set<string>();
    for (const shipment of basket.shipments ?? []) {
        if (shipmentIsPickup(shipment) && shipment.shipmentId) {
            ids.add(shipment.shipmentId);
        }
    }
    return ids;
}

/**
 * Derives the Ashley `CartType` from a basket.
 *
 * Rules (in order):
 *   1. Empty basket → `undefined`.
 *   2. Any pickup line AND any delivery line → `Mixed`.
 *   3. All lines pickup → `Pickup`.
 *   4. Otherwise pick the majority `c_deliveryMethod` among the delivery
 *      lines and map to the human label.
 *   5. If no line has `c_deliveryMethod` set, fall back to the
 *      shipment-only signal (`c_fromStoreId` presence) so callers still
 *      get a coarse answer during the brief window between add-to-cart
 *      and the CART-02 enrichment writing `c_deliveryMethod`.
 */
export function getCartType(basket?: Basket): CartType | undefined {
    if (!basket) return undefined;

    const productItems = basket.productItems ?? [];
    const shipments = basket.shipments ?? [];
    if (productItems.length === 0 && shipments.length === 0) return undefined;

    const pickupShipmentIds = buildPickupShipmentIdSet(basket);

    let pickupLineCount = 0;
    let deliveryLineCount = 0;
    const deliveryMethodTally = new Map<DeliveryMethodCode, number>();

    for (const item of productItems) {
        const isPickup = Boolean(item.shipmentId && pickupShipmentIds.has(item.shipmentId));
        if (isPickup) {
            pickupLineCount += 1;
            continue;
        }
        deliveryLineCount += 1;
        const method = readDeliveryMethod(item);
        if (method) {
            deliveryMethodTally.set(method, (deliveryMethodTally.get(method) ?? 0) + 1);
        }
    }

    // Fall back to shipment-only signal if the basket has no productItems yet
    // (e.g. mid-transition during a fetch-driven revalidation).
    if (productItems.length === 0) {
        const hasPickup = shipments.some(shipmentIsPickup);
        const hasDelivery = shipments.some((s) => !shipmentIsPickup(s));
        if (hasPickup && hasDelivery) return 'Mixed';
        if (hasPickup) return 'Pickup';
        return 'Home Delivery';
    }

    if (pickupLineCount > 0 && deliveryLineCount > 0) return 'Mixed';
    if (pickupLineCount > 0) return 'Pickup';

    // All delivery — pick the majority delivery method label.
    if (deliveryMethodTally.size === 0) {
        // No `c_deliveryMethod` populated yet; default to Home Delivery so
        // downstream GA3 dashboards get a stable value instead of a blank.
        return 'Home Delivery';
    }

    let winner: DeliveryMethodCode = 'HD';
    let winnerCount = -1;
    for (const [code, count] of deliveryMethodTally) {
        if (count > winnerCount) {
            winner = code;
            winnerCount = count;
        }
    }
    return CART_TYPE_LABELS[winner];
}

/**
 * Returns the legacy short-code alias for a given `CartType`. Used to emit
 * `cart_type_code` alongside the human label on `setCartType` when
 * `emitLegacyAliases !== false`.
 */
export function getCartTypeCode(cartType: CartType): CartTypeCode {
    return CART_TYPE_CODES[cartType];
}

export interface SetCartTypeOptions {
    /** When false, suppress the legacy `cart_type_code` field. Default true. */
    emitLegacyAliases?: boolean;
}

/**
 * Pushes the GA4-canonical `set_cart_type` event AND, unless
 * `emitLegacyAliases === false`, a second legacy `setCartType` (camelCase)
 * push for GA3 tag continuity.
 *
 * Both pushes carry the human label as `cartType` and, when legacy aliases are
 * on, the numeric short-code as `cart_type_code`.
 */
export function pushSetCartType(cartType: CartType, options: SetCartTypeOptions = {}): void {
    const emitLegacyAliases = options.emitLegacyAliases !== false;
    const payload: Record<string, unknown> = {
        event: 'set_cart_type',
        event_name: 'set_cart_type',
        cartType,
        cart_type: cartType,
    };
    if (emitLegacyAliases) {
        payload.cart_type_code = getCartTypeCode(cartType);
    }
    pushEvent(payload);

    if (!emitLegacyAliases) return;

    // Legacy camelCase alias — kept for GA3 tag continuity.
    pushEvent({
        event: 'setCartType',
        cartType,
        cart_type_code: getCartTypeCode(cartType),
    });
}

/**
 * Pushes global session data to dataLayer. Should be called on basket/auth state changes.
 * This mirrors the legacy googletagmanager_globalsessionelements.isml behavior.
 * Not an event — just a data push that GTM reads as variables.
 *
 * `options.emitLegacyAliases` is threaded through to the piggybacked
 * `setCartType` push so the numeric `cart_type_code` follows the same
 * config toggle as the rest of the GA3 aliases.
 *
 * INT-21 Phase F adds:
 *   - `dimension17` (session ZIP) + `user_properties.zip_code`
 *   - `dimension21` (is-returning-customer as GA3 string boolean '0'|'1')
 *     + `user_properties.is_returning_customer` (real boolean)
 *   - `dimension142` (basket.basketId) — legacy GA3 alias
 *   - `dimension143` (short-code cart type e.g. HD/PHD/DS/PICKUP/MIXED) — legacy alias
 *   - `sfcc_customer_id` GA4-friendly alias for demandwareID
 *
 * All `dimensionNN` aliases are gated by `options.emitLegacyAliases !== false`.
 * The GA4 `user_properties.*` fields fire unconditionally when the source
 * data is present (they are GA4-canonical, not legacy).
 */
export function pushGlobalSessionData(data: GlobalSessionData, options: SetCartTypeOptions = {}): void {
    const productItems = data.basket?.productItems ?? [];
    const itemCount = productItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    const emitLegacyAliases = options.emitLegacyAliases !== false;
    const basketCartType = data.basket ? getCartType(data.basket) : undefined;
    const basketCartId = data.basket?.basketId;
    const uaCartScope = basketCartId ? { cartId: basketCartId, cartType: basketCartType } : undefined;

    // Legacy `Site_TagManager.getGlobalData` shape: `basket` is an ARRAY of
    // per-line UA-shape product objects (built via
    // `Util.getProductArrayFromList(iter, Site_TagManager.getOrderProductObject)`).
    // We emit the same array here so legacy tag builders that iterate the
    // `basket` field continue to work. The itemCount/subtotal summary object
    // is kept under `basketSummary` so anything reading that today still works.
    const basketArray = productItems.map((item, index) =>
        buildUaProduct(item, item.quantity ?? 1, item.price ?? item.basePrice, {
            position: index + 1,
            cartScope: uaCartScope,
            cartId: basketCartId,
            cartType: basketCartType ? getCartTypeCode(basketCartType) : undefined,
            emitLegacyAliases,
        })
    );

    const payload: Record<string, unknown> = {
        loggedInState: data.loggedInState,
        logged_in_state: data.loggedInState,
        basket: basketArray,
        basketSummary: {
            itemCount,
            subtotal: data.basket?.productSubTotal ?? 0,
            totalShipping: data.basket?.shippingTotal ?? 0,
            totalTax: data.basket?.taxTotal ?? 0,
            currency: data.basket?.currency ?? 'USD',
        },
        // Legacy `Site_TagManager.getGlobalData` roots (mirror the pre-basket-array
        // fields at the top-level of the push, not nested).
        subTotalWithPP: data.basket?.productTotal ?? data.basket?.productSubTotal ?? 0,
        basketTotalShipping: data.basket?.shippingTotal ?? 0,
        basketTotalTax: data.basket?.taxTotal ?? 0,
        pageLanguage: data.locale,
        language: data.locale,
    };

    // GA4 `user_properties` collector — populated below and pushed only when
    // it has at least one entry, so we don't emit empty objects.
    const userProperties: Record<string, unknown> = {};

    if (data.customerId) {
        payload.demandwareID = data.customerId;
        payload.sfcc_customer_id = data.customerId;
    }
    if (data.emailsha256) {
        payload.emailsha256 = data.emailsha256;
    }

    // Phase F.1 — dimension17 (session ZIP) + user_properties.zip_code.
    if (data.zip) {
        userProperties.zip_code = data.zip;
        payload.zip_code = data.zip;
        if (emitLegacyAliases) payload.dimension17 = data.zip;
    }

    // Phase F.2 — dimension21 (is-returning-customer). Legacy shape is a
    // string boolean ('0' | '1'), GA4 shape is a real boolean under
    // user_properties. Both are emitted so tag builders can pick their side.
    if (typeof data.isReturningCustomer === 'boolean') {
        userProperties.is_returning_customer = data.isReturningCustomer;
        payload.is_returning_customer = data.isReturningCustomer;
        if (emitLegacyAliases) payload.dimension21 = data.isReturningCustomer ? '1' : '0';
    }

    // Phase F.3 — dimension142 (cartId).
    if (data.basket?.basketId && emitLegacyAliases) {
        payload.dimension142 = data.basket.basketId;
    }

    // Phase F.3 — dimension143 (cart type short code, e.g. HD/PHD/DS/PICKUP/MIXED).
    // Derived from the broadened `getCartType` helper (Phase B).
    if (basketCartType && emitLegacyAliases) {
        payload.dimension143 = getCartTypeCode(basketCartType);
    }

    if (Object.keys(userProperties).length > 0) {
        payload.user_properties = userProperties;
    }

    pushEvent(payload);

    // Fire setCartType when basket has items and cart type can be derived
    if (basketCartType) {
        pushSetCartType(basketCartType, options);
    }
}
